export const timelineNarrationTrackIds = ['en', 'zh-Hant-mandarin'] as const
const timelineNarrationDormantTrackIds = ['zh-Hant-cantonese'] as const

export type TimelineNarrationActiveTrackId = typeof timelineNarrationTrackIds[number]
type TimelineNarrationDormantTrackId = typeof timelineNarrationDormantTrackIds[number]
export type TimelineNarrationTrackId = TimelineNarrationActiveTrackId | TimelineNarrationDormantTrackId
export type TimelineNarrationMode = TimelineNarrationActiveTrackId | 'off'

export interface TimelineNarrationTrack {
  id: TimelineNarrationTrackId
  label: {
    en: string
    'zh-Hant': string
  }
  azureLocale: string
  azureVoice: string
  azureStyle?: string
  prosodyRate?: string
  text: string
  ssml?: string
  src: string
  estimatedDurationSeconds: number
}

export interface TimelineNarrationScene {
  sceneId: string
  globalOrder: number
  storyId: string
  stepId: string
  sourceStepIds: string[]
  sourceIds: string[]
  tracks: Record<TimelineNarrationActiveTrackId, TimelineNarrationTrack> & Partial<Record<TimelineNarrationDormantTrackId, TimelineNarrationTrack>>
}

export const timelineNarrationOptions: Array<{
  id: TimelineNarrationMode
  label: {
    en: string
    'zh-Hant': string
  }
}> = [
  { id: 'off', label: { en: 'Off', 'zh-Hant': '關閉' } },
  { id: 'en', label: { en: 'English', 'zh-Hant': 'English' } },
  { id: 'zh-Hant-mandarin', label: { en: 'Chinese', 'zh-Hant': '中文' } },
]

const routeId = 'greek-core-eight-ages'
const audioRoot = `audio/timeline/${routeId}`
const labels = {
  en: { en: 'English', 'zh-Hant': 'English' },
  mandarin: { en: 'Chinese', 'zh-Hant': '中文' },
  cantonese: { en: 'Cantonese', 'zh-Hant': '廣東話' },
}

const track = (
  sceneId: string,
  id: TimelineNarrationTrackId,
  config: Omit<TimelineNarrationTrack, 'id' | 'src'>,
): TimelineNarrationTrack => ({
  id,
  src: `${audioRoot}/${sceneId}/${id}.mp3`,
  ...config,
})

export const timelineNarrationScenes: TimelineNarrationScene[] = [
  {
    sceneId: "theogony-theogony-first-powers",
    globalOrder: 1,
    storyId: "theogony",
    stepId: "theogony-first-powers",
    sourceStepIds: ["theogony-first-powers"],
    sourceIds: ["theogony-116-125-first-powers", "iliad-14-201-246-oceanus-origin"],
    tracks: {
      en: track("theogony-theogony-first-powers", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 26,
        text: "Before Zeus, before Olympus, the story opens with a gap. Chaos appears first; then Gaia, Tartarus and Eros enter the world, while Night and Erebus come from Chaos. In this opening, Greek myth begins with powers taking shape, not with a king giving orders.",
      }),
      'zh-Hant-mandarin': track("theogony-theogony-first-powers", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 27,
        text: "最初還沒有宙斯，也沒有奧林匹斯。故事先從卡俄斯開始；之後，蓋亞、塔耳塔羅斯與厄洛斯相繼出現，倪克斯與厄瑞玻斯也由卡俄斯而來。希臘神話的開端，先是力量各自成形，而不是王者下令創造世界。",
      }),
    },
  },
  {
    sceneId: "theogony-theogony-earth-sky-sea",
    globalOrder: 2,
    storyId: "theogony",
    stepId: "theogony-earth-sky-sea",
    sourceStepIds: ["theogony-earth-sky-sea"],
    sourceIds: ["theogony-126-153-gaia-uranus-children"],
    tracks: {
      en: track("theogony-theogony-earth-sky-sea", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 22,
        text: "The next turn belongs to Gaia. She brings forth Uranus, the sky that covers her, and Pontus, the sea at the world's edge. The scene is still not a war for power. It gives the world shape, so the first divine family can begin.",
      }),
      'zh-Hant-mandarin': track("theogony-theogony-earth-sky-sea", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 23,
        text: "下一個轉向落在蓋亞身上。她從自身生出烏拉諾斯，讓天空覆在大地之上；又生出蓬托斯，讓海在世界邊緣展開。這裏還不是爭權，而是世界先有了形狀，第一個神族家庭才有地方開始。",
      }),
    },
  },
  {
    sceneId: "gaia-uranus-children-and-confinement",
    globalOrder: 3,
    storyId: "theogony",
    stepId: "theogony-children-earth-sky",
    sourceStepIds: ["theogony-children-earth-sky", "theogony-hidden-within-earth"],
    sourceIds: ["theogony-126-153-gaia-uranus-children", "apollodorus-library-1-1-1-7", "theogony-154-187-uranus-overthrow"],
    tracks: {
      en: track("gaia-uranus-children-and-confinement", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 28,
        text: "When Gaia and Uranus unite, the world becomes a family. Titans, Cyclopes and Hundred-Handers are born, but Uranus hides the terrifying children inside Gaia. Birth turns into pressure. Gaia's pain gives the next event its force: rebellion will need an instrument, and a child willing to act.",
      }),
      'zh-Hant-mandarin': track("gaia-uranus-children-and-confinement", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 29,
        text: "蓋亞與烏拉諾斯結合後，世界開始變成一個家族。泰坦、獨眼巨人與百臂巨人相繼出生；可是烏拉諾斯把可怕的子女藏回蓋亞深處。生育變成壓迫。蓋亞的痛苦，令下一場反抗有了理由，也需要一件工具和一個願意出手的孩子。",
      }),
    },
  },
  {
    sceneId: "theogony-theogony-cronus-takes-sickle",
    globalOrder: 4,
    storyId: "theogony",
    stepId: "theogony-cronus-takes-sickle",
    sourceStepIds: ["theogony-cronus-takes-sickle"],
    sourceIds: ["theogony-154-187-uranus-overthrow"],
    tracks: {
      en: track("theogony-theogony-cronus-takes-sickle", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 25,
        text: "Gaia makes the sickle and asks her children who will strike Uranus. The others draw back; Cronus answers. He waits in ambush until sky comes down over earth again, then cuts open the first divine rule. Uranus falls, and succession becomes a problem no ruler can ignore.",
      }),
      'zh-Hant-mandarin': track("theogony-theogony-cronus-takes-sickle", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 26,
        text: "蓋亞造出鐮刀，問自己的子女誰願意向烏拉諾斯出手。其他孩子退縮，克羅諾斯答應。當天空再次覆向大地，他從伏藏處揮下鐮刀，切開第一個神族統治。烏拉諾斯倒下，承繼問題從此再也不能被王者忽視。",
      }),
    },
  },
  {
    sceneId: "theogony-theogony-aphrodite-from-foam",
    globalOrder: 5,
    storyId: "theogony",
    stepId: "theogony-aphrodite-from-foam",
    sourceStepIds: ["theogony-aphrodite-from-foam"],
    sourceIds: ["theogony-aphrodite-birth", "iliad-5-370-380-aphrodite-dione", "iliad-5-130-132-aphrodite-zeus"],
    tracks: {
      en: track("theogony-theogony-aphrodite-from-foam", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 29,
        text: "Uranus is overthrown, but the story stays with what his fall leaves behind. The severed part is cast into the sea; foam gathers, and Aphrodite emerges with Eros in attendance. This is Hesiod's route. The Iliad keeps another parentage tradition, so this birth belongs to one ancient version rather than every account.",
      }),
      'zh-Hant-mandarin': track("theogony-theogony-aphrodite-from-foam", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 30,
        text: "烏拉諾斯被推翻後，故事仍停在那場暴力的餘波之中。被割下的部分被投入海中，泡沫聚集，阿佛洛狄忒由此出現，厄洛斯伴隨她而行。這是赫西俄德《神譜》的路線；《伊利亞特》保留另一組父母說法，所以這次誕生只屬其中一條古代版本。",
      }),
    },
  },
  {
    sceneId: "old-divine-branches",
    globalOrder: 6,
    storyId: "theogony",
    stepId: "theogony-houses-night-sea",
    sourceStepIds: ["theogony-houses-night-sea", "theogony-older-branches-honours"],
    sourceIds: ["theogony-211-232-night-children", "theogony-233-336-sea-children", "theogony-901-906-zeus-themis-moirai", "theogony-337-403-titan-branches-styx", "theogony-404-452-hecate-honours", "odyssey-7-245-260-calypso-atlas"],
    tracks: {
      en: track("old-divine-branches", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 42,
        text: "Titan rule is not an empty interval. Nyx's children bring the Fates, Hypnos, Thanatos, and Eris into mythic language, while Pontus, Oceanus, and Tethys carry the story toward sea powers, boundaries, and future allies. Styx, Calypso, and Hecate will return later with distinct force. These names are not passing details; later oaths, honours, underworld scenes, and heroic journeys repeatedly return to these older powers.",
      }),
      'zh-Hant-mandarin': track("old-divine-branches", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 55,
        text: "泰坦統治並不是空白年代。克羅諾斯掌權後，世界沒有只剩下他與泰坦；夜、海和更古老的神明仍各自延續，許多後來會反覆出現的力量，也在這時逐漸成形。倪克斯的家系帶出摩伊賴、許普諾斯、塔納托斯與厄里斯，令命運、睡眠、死亡與紛爭很早進入希臘神話。蓬托斯、俄刻阿諾斯與忒堤斯則把故事帶向海域、邊界與誓言；斯堤克斯日後會首先響應宙斯，赫卡忒也會在新秩序中保留並擴展權位。卡呂普索的身份要分開看：赫西俄德名錄中的卡呂普索，未必就是《奧德賽》的奧吉吉亞女神。這些名字不是路過的細節；後來的誓言、榮位、亡者世界與英雄旅程，都會再次回到這些較早出現的神明與力量。",
      }),
    },
  },
  {
    sceneId: "rheas-children-and-cronus-fear",
    globalOrder: 7,
    storyId: "theogony",
    stepId: "theogony-swallowed-children",
    sourceStepIds: ["theogony-swallowed-children"],
    sourceIds: ["theogony-453-467-cronus-children"],
    tracks: {
      en: track("rheas-children-and-cronus-fear", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 44,
        text: "After Cronus takes power, he has not escaped the shadow of succession. Gaia and Uranus foretell that one of his own children will take his honour. Rhea bears Hestia, Demeter, Hera, Hades, and Poseidon. Each newborn is swallowed by Cronus, as if enclosing the next generation in himself could keep the prophecy from becoming real. This is not a simple family list but the opening of the second succession crisis.",
      }),
      'zh-Hant-mandarin': track("rheas-children-and-cronus-fear", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 54,
        text: "克羅諾斯接過王權後，並沒有真正擺脫承繼陰影。蓋亞與烏拉諾斯的預言指出，他也會被自己的孩子奪去榮位。瑞亞先後生下赫斯提亞、得墨忒耳、赫拉、哈得斯與波塞冬。每一個孩子剛出生，克羅諾斯便把他吞下，彷彿只要把下一代困在自己體內，預言就無法抵達現實。這不是普通家族名錄，而是第二次承繼危機的開場。五名未來奧林匹斯神消失在父親體內，瑞亞的悲痛也把故事推向最後一個孩子：宙斯。",
      }),
    },
  },
  {
    sceneId: "theogony-theogony-hidden-child",
    globalOrder: 8,
    storyId: "theogony",
    stepId: "theogony-hidden-child",
    sourceStepIds: ["theogony-hidden-child"],
    sourceIds: ["theogony-468-491-hidden-zeus", "apollodorus-library-1-1-1-7"],
    tracks: {
      en: track("theogony-theogony-hidden-child", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 40,
        text: "When the youngest child is about to be born, Rhea is no longer only grieving. She seeks help from Gaia and Uranus to keep the child alive. Zeus is hidden and raised away from Cronus. Rhea wraps a stone in swaddling cloth and gives it to the fearful king, who swallows it believing the future is contained again. Zeus does not overthrow his father at once, but he survives.",
      }),
      'zh-Hant-mandarin': track("theogony-theogony-hidden-child", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 39,
        text: "到了最幼的孩子將要出生時，瑞亞不再只是哀傷。她向蓋亞與烏拉諾斯求助，尋找能讓孩子活下來的方法。新生的宙斯被送到隱藏處撫養；瑞亞則把一塊石頭包在襁褓裏，交給克羅諾斯。害怕預言的王吞下石頭，以為又一次把未來鎖在自己體內。宙斯沒有即時推翻父親，但他被保存下來。",
      }),
    },
  },
  {
    sceneId: "zeus-return-and-succession-war",
    globalOrder: 9,
    storyId: "theogony",
    stepId: "theogony-stone-siblings-thunder",
    sourceStepIds: ["theogony-stone-siblings-thunder", "titanomachy-succession-war"],
    sourceIds: ["theogony-492-506-release-thunderbolt", "apollodorus-library-1-2-1", "theogony-453-467-cronus-children", "theogony-468-491-hidden-zeus"],
    tracks: {
      en: track("zeus-return-and-succession-war", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 46,
        text: "When the hidden Zeus grows up, Cronus' strategy of swallowing children reverses. The future he kept inside himself is forced back into the world. Rhea and Gaia open the way; in the Bibliotheca, Metis also helps with a drug. The swallowed siblings return, the Cyclopes are freed, and thunder comes to Zeus. Succession is no longer only a prophecy. Family conflict, released allies, and Titan resistance gather into the Titanomachy.",
      }),
      'zh-Hant-mandarin': track("zeus-return-and-succession-war", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 53,
        text: "被藏起的宙斯長大後，克羅諾斯的吞子策略終於反轉。王曾經把未來吞進腹中，現在腹中的未來要被迫回到世界。瑞亞與蓋亞的計策打開道路；《書庫》還加入墨提斯，她以藥劑協助，令克羅諾斯吐出石頭與手足。赫斯提亞、得墨忒耳、赫拉、哈得斯與波塞冬重回故事，獨眼巨人也從束縛中得自由，雷霆交到宙斯手上。承繼危機因此不再只是預言。父子衝突、被釋放的盟友與泰坦神族的反抗一起匯成泰坦之戰。",
      }),
    },
  },
  {
    sceneId: "olympus-othrys-deadlock",
    globalOrder: 10,
    storyId: "titanomachy",
    stepId: "titanomachy-olympus-othrys",
    sourceStepIds: ["titanomachy-olympus-othrys", "titanomachy-ten-year-deadlock"],
    sourceIds: ["theogony-126-153-gaia-uranus-children", "theogony-617-643-war-camps-counsel", "apollodorus-library-1-2-1"],
    tracks: {
      en: track("olympus-othrys-deadlock", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 43,
        text: "Once the Titanomachy begins, two mountains mark the field. Zeus' side holds Mount Olympus, while the Titans answer from Mount Othrys. This is not a brief clash but a long deadlock. The conflict between Cronus and Zeus becomes a confrontation between divine generations, and the rescued siblings become part of the new coalition. After years of stalemate, bloodline alone cannot decide the war.",
      }),
      'zh-Hant-mandarin': track("olympus-othrys-deadlock", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 53,
        text: "泰坦之戰開始後，戰場被兩座山標示出來。宙斯一方據守奧林匹斯山，泰坦神族則從俄特律斯山迎戰。這不是一次短促衝突，而是長久僵局。克羅諾斯與宙斯的父子問題，被放大成兩代神祇的權力對峙；赫斯提亞、得墨忒耳、赫拉、哈得斯與波塞冬也不再只是被救出的手足，而是新陣營的一部分。十年相持令勝敗無法只靠血統決定。若沒有新的盟友與武器，世界會繼續停在奧林匹斯與俄特律斯之間。",
      }),
    },
  },
  {
    sceneId: "gaia-condition-and-hundred-handers",
    globalOrder: 11,
    storyId: "titanomachy",
    stepId: "titanomachy-gaia-condition",
    sourceStepIds: ["titanomachy-gaia-condition", "titanomachy-hundred-handers-covenant"],
    sourceIds: ["theogony-617-643-war-camps-counsel", "apollodorus-library-1-2-1", "theogony-644-663-cottus-covenant"],
    tracks: {
      en: track("gaia-condition-and-hundred-handers", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 44,
        text: "To break the deadlock, Zeus must return to the deepest wound in the family. Powers once shut into Tartarus still remain below the world. Gaia says the Hundred-Handers can change the war. Zeus confronts Campe, releases them, and binds their strength to the aid of the Cyclopes. From here, the answer to the war is not simply on the mountains but in the depths. A new order depends on dealing with the old wound.",
      }),
      'zh-Hant-mandarin': track("gaia-condition-and-hundred-handers", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 50,
        text: "僵局要被打破，宙斯必須先回到家族最深的傷口。上一代被關入塔耳塔羅斯的力量，仍然留在世界下方。蓋亞指出百臂巨人能改變戰局。宙斯於是面對看守坎佩，釋放百臂巨人，也讓獨眼巨人的恩義與戰爭連在一起。從這裡開始，戰爭的答案不再只在山上，而在深處。誰能處理被壓抑的舊傷，誰才可能建立新的秩序；被囚者的釋放，也讓戰爭第一次有了真正改變局面的可能。",
      }),
    },
  },
  {
    sceneId: "titanomachy-titanomachy-styx-first",
    globalOrder: 12,
    storyId: "titanomachy",
    stepId: "titanomachy-styx-first",
    sourceStepIds: ["titanomachy-styx-first"],
    sourceIds: ["theogony-383-403-styx-alliance", "apollodorus-library-1-2-3-5"],
    tracks: {
      en: track("titanomachy-titanomachy-styx-first", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 44,
        text: "The war needs more than weapons; it needs the first public choice of sides. Nike, Kratos, Bia, and Zelus join with their mother, placing victory, strength, force, and zeal beside the future king. Oceanus and Tethys' side is also drawn toward Zeus' coalition. Before victory, Zeus is already turning loyalty into future honours.",
      }),
      'zh-Hant-mandarin': track("titanomachy-titanomachy-styx-first", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 54,
        text: "戰爭需要的不再只是武器，也需要誰先願意站隊。斯堤克斯聽從俄刻阿諾斯的建議，帶着自己與帕拉斯的子女來到宙斯一方。尼刻、克拉托斯、比亞與澤洛斯隨母親加入，令「勝利、力量、強制與競爭」這些抽象力量都站到新王候選者身邊。俄刻阿諾斯與忒堤斯這一邊，也因此被拉向宙斯陣營。在戰爭真正決出勝負前，宙斯已經開始把忠誠轉成日後的榮位。",
      }),
    },
  },
  {
    sceneId: "titanomachy-titanomachy-three-weapons",
    globalOrder: 13,
    storyId: "titanomachy",
    stepId: "titanomachy-three-weapons",
    sourceStepIds: ["titanomachy-three-weapons"],
    sourceIds: ["theogony-492-506-release-thunderbolt", "apollodorus-library-1-2-1"],
    tracks: {
      en: track("titanomachy-titanomachy-three-weapons", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 40,
        text: "Once the allies return, the Titanomachy gains new force. The Cyclopes, once imprisoned below, bring divine craft onto the battlefield. Hesiod has them give Zeus thunder and lightning. In the Bibliotheca, the three brothers each receive a weapon: Zeus the thunderbolt, Poseidon the trident, and Hades the helmet; Metis also gives Cronus the drug that brings the swallowed children back.",
      }),
      'zh-Hant-mandarin': track("titanomachy-titanomachy-three-weapons", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 45,
        text: "盟友回到地面後，泰坦之戰得到新的力量。獨眼巨人曾在深處受困，獲釋後以神工把雷霆交給戰場。在赫西俄德的說法中，獨眼巨人把雷、閃電與雷霆交給宙斯；《書庫》另寫三兄弟各得一件武器：宙斯掌雷霆，波塞冬得三叉戟，哈得斯得頭盔。同一版本也寫到墨提斯：她給克羅諾斯藥物，使被吞下的孩子重回世界。",
      }),
    },
  },
  {
    sceneId: "titanomachy-titanomachy-heaven-earth-boil",
    globalOrder: 14,
    storyId: "titanomachy",
    stepId: "titanomachy-heaven-earth-boil",
    sourceStepIds: ["titanomachy-heaven-earth-boil"],
    sourceIds: ["theogony-664-686-decisive-clash", "theogony-687-735-thunder-stones-defeat"],
    tracks: {
      en: track("titanomachy-titanomachy-heaven-earth-boil", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 44,
        text: "The stalemate between Mount Olympus and Mount Othrys is pushed to its limit. The Titans face not simply a rival claimant but the decisive force of the Hundred-Handers. Fire, sound, and stones press the battlefield toward collapse; the Titanomachy becomes a cosmic rearrangement. Once heaven and earth boil, victory cannot return to the old balance. The next question is where the defeated will be placed.",
      }),
      'zh-Hant-mandarin': track("titanomachy-titanomachy-heaven-earth-boil", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 52,
        text: "奧林匹斯山與俄特律斯山之間的僵持終於被推到極限。宙斯不再收斂雷霆，戰爭從兩軍對峙變成天地震動。泰坦神族面對的不再只是新王候選者，而是百臂巨人加入後的決戰。火、聲響與震動遍及天空與大地；百臂巨人連續投出巨石，把戰線壓向崩潰。當天地沸騰，勝利也不再能回到舊平衡。下一章要處理的不是誰更強，而是戰敗者將被放到哪裏，以及新秩序要用甚麼方式防止舊王朝回來。",
      }),
    },
  },
  {
    sceneId: "titans-fall-and-tartarus-guarded",
    globalOrder: 15,
    storyId: "titanomachy",
    stepId: "titanomachy-titans-fall",
    sourceStepIds: ["titanomachy-titans-fall", "titanomachy-gates-tartarus"],
    sourceIds: ["theogony-507-520-iapetus-sons", "theogony-687-735-thunder-stones-defeat", "apollodorus-library-1-2-3-5", "theogony-736-806-tartarus-gates", "theogony-807-819-titans-guards", "apollodorus-library-1-2-1"],
    tracks: {
      en: track("titans-fall-and-tartarus-guarded", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 42,
        text: "After thunder and stones break the stalemate, Titan rule ends. Zeus does not defeat only Cronus; he must decide what happens to the whole old regime. The defeated are bound and sent into Tartarus, guarded by the Hundred-Handers. The sons of Iapetus receive individual fates: Menoetius is struck down, while Atlas is assigned the sky burden. Victory is therefore not simply celebration.",
      }),
      'zh-Hant-mandarin': track("titans-fall-and-tartarus-guarded", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 50,
        text: "雷霆與巨石打破僵局後，泰坦神族的統治走到盡頭。宙斯不再只是擊敗克羅諾斯，也必須處理整個舊王朝的去向。戰敗者被捆縛並送入塔耳塔羅斯深處；百臂巨人轉為門前看守，使戰爭成果變成持續封鎖。伊阿佩托斯的兒子也各有命運：墨諾提俄斯被雷霆擊下，阿特拉斯被分派承受天空。勝利因此不再只是慶祝，而是安排懲罰、邊界與守衛。新的宇宙政治從囚禁舊神開始穩固。",
      }),
    },
  },
  {
    sceneId: "typhoeus-and-secured-throne",
    globalOrder: 16,
    storyId: "olympians",
    stepId: "olympians-after-victory",
    sourceStepIds: ["olympians-after-victory", "olympians-earth-last-challenge", "olympians-thunder-secures-throne"],
    sourceIds: ["theogony-687-735-thunder-stones-defeat", "theogony-807-819-titans-guards", "theogony-820-852-typhoeus-rises", "theogony-853-885-typhoeus-kingship-honours", "apollodorus-library-1-6-3-typhon-variant"],
    tracks: {
      en: track("typhoeus-and-secured-throne", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 43,
        text: "The Titans have been confined in Tartarus, but Zeus' victory is not yet a settled kingship. The postwar world still faces one last test. Gaia and Tartarus produce Typhoeus, a threat that rises against Olympus and the edges of the cosmos. Zeus defeats him with thunder and casts him into the depths. Only after this last challenge is contained does kingship become more than the result of battle.",
      }),
      'zh-Hant-mandarin': track("typhoeus-and-secured-throne", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 53,
        text: "泰坦神族已被囚在塔耳塔羅斯，但宙斯的勝利仍未完全變成穩定王權。戰爭後的世界還要經過最後一次試煉。蓋亞與塔耳塔羅斯生下堤豐，把威脅推到奧林匹斯山與天地邊界面前。這不是泰坦之戰的普通餘波，而是新王權是否能抵抗最混亂力量的考驗。最後挑戰被壓下後，王位才不再只是戰爭勝利的結果，而成為能鎮住天地、深淵與怪物的秩序中心；戰後秩序仍要經過最後一次驗證，才算真正站穩。",
      }),
    },
  },
  {
    sceneId: "zeus-kingship-and-realms",
    globalOrder: 17,
    storyId: "olympians",
    stepId: "olympians-gods-choose-zeus",
    sourceStepIds: ["olympians-gods-choose-zeus", "olympians-realms-by-lot"],
    sourceIds: ["theogony-853-885-typhoeus-kingship-honours", "apollodorus-library-1-2-1"],
    tracks: {
      en: track("zeus-kingship-and-realms", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 42,
        text: "Defeating enemies is not enough to be king. The gods choose Zeus as king, and the Olympian Council and Mount Olympus become the centre of order. Pseudo-Apollodorus also explains the threefold division by lot: Zeus receives the sky, Poseidon the sea, and Hades the Underworld. From here, the mythic question changes from who overthrows whom to what honour each god holds in the new world.",
      }),
      'zh-Hant-mandarin': track("zeus-kingship-and-realms", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 46,
        text: "擊敗敵人仍不足以成王。大戰之後，宙斯需要眾神承認，也需要把戰後世界分出清楚邊界。眾神依蓋亞提示推舉宙斯為王，奧林匹斯議會與奧林匹斯山因此成為新秩序中心。偽阿波羅多洛斯另以抽籤說明三兄弟的分權：宙斯得天空，波塞冬得海洋，哈得斯得冥界。從這裏開始，希臘神話不再只問誰推翻誰，而要問每位神在新世界中佔有甚麼榮位。",
      }),
    },
  },
  {
    sceneId: "olympians-olympians-swallowing-succession",
    globalOrder: 18,
    storyId: "olympians",
    stepId: "olympians-swallowing-succession",
    sourceStepIds: ["olympians-swallowing-succession"],
    sourceIds: ["theogony-886-900-metis-succession", "theogony-918-929-olympian-offspring", "homeric-hymn-28-1-15-athena-birth"],
    tracks: {
      en: track("olympians-olympians-swallowing-succession", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 40,
        text: "At the beginning of the Olympian Order, Zeus faces the same question as his father and grandfather: can kingship escape the next succession crisis? He marries Metis, but Gaia and Uranus warn that she will bear Athena and then a son who could overthrow him. Zeus deceives Metis and swallows her, taking wisdom and danger into himself. The new order is not without fear.",
      }),
      'zh-Hant-mandarin': track("olympians-olympians-swallowing-succession", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 47,
        text: "奧林匹斯秩序剛建立時，宙斯仍面對與父祖相同的問題：王權能否逃過下一次承繼危機？他迎娶最機敏的墨提斯，卻從蓋亞與烏拉諾斯得知新的預言：墨提斯會先誕下雅典娜，之後還可能生出足以奪位的兒子。宙斯沒有像克羅諾斯那樣吞下已出生的孩子，而是以話語欺瞞，把墨提斯吞入自己體內，把智慧與危險同時收進新王身上。新秩序並非沒有恐懼。",
      }),
    },
  },
  {
    sceneId: "song-justice-and-fate",
    globalOrder: 19,
    storyId: "theogony",
    stepId: "theogony-muses-commission",
    sourceStepIds: ["theogony-muses-commission", "olympians-order-justice-fate"],
    sourceIds: ["theogony-1-115-muses", "theogony-901-906-zeus-themis-moirai", "theogony-211-232-night-children"],
    tracks: {
      en: track("song-justice-and-fate", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 40,
        text: "After kingship is established, Olympus needs more than force. It needs language that can remember, sing, and distribute justice. The Muses make divine origins tellable, while Zeus and Themis place the Horae and the Fates beside kingship. Order, justice, peace, and the allotment of life receive sacred positions. Olympus is therefore not simply where the victors gather.",
      }),
      'zh-Hant-mandarin': track("song-justice-and-fate", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 46,
        text: "王權建立後，奧林匹斯需要的不再只是武力，也需要能被記住、能被唱出、能分配正義的語言。繆斯於是站在故事入口。繆斯讓眾神起源成為可傳述的詩；宙斯與忒彌斯的家系則把時序女神與摩伊賴放入王權旁邊，使良序、正義、和平與生命分配都有神聖位置。奧林匹斯因此不再只是勝利者聚居之處，也成為記憶、法律與命運互相扣連的中心。",
      }),
    },
  },
  {
    sceneId: "olympians-olympians-grace-memory-seasons",
    globalOrder: 20,
    storyId: "olympians",
    stepId: "olympians-grace-memory-seasons",
    sourceStepIds: ["olympians-grace-memory-seasons"],
    sourceIds: ["theogony-907-918-divine-households", "theogony-1-115-muses"],
    tracks: {
      en: track("olympians-olympians-grace-memory-seasons", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 45,
        text: "As Zeus' household expands, the Olympian Order fills with powers of daily life, not simply war and kingship. Eurynome bears the Charites, Mnemosyne bears the Muses, and the line of Demeter and Persephone is drawn below through Hades' taking. Beauty, memory, song, seasons, and return enter the order. The point is not a list of names but the spread of order into feasting, poetry, land, and the rhythm of the year.",
      }),
      'zh-Hant-mandarin': track("olympians-olympians-grace-memory-seasons", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 52,
        text: "宙斯的家族繼續展開時，奧林匹斯秩序逐步填入更細的生活力量，不單處理戰爭和王權。歐律諾墨生下美惠三女神，讓美、喜悅與吸引進入神族榮位；姆涅摩緒涅生下繆斯，把記憶與歌聲安放在眾神旁邊。得墨忒耳與珀耳塞福涅的母女線，則因哈得斯的奪取而連到地下，將季節、婚姻與失而復歸的節律帶入世界。重點不在列名，而在顯示新秩序如何由王權擴散到宴飲、詩歌、土地與一年循環。",
      }),
    },
  },
  {
    sceneId: "olympian-children-and-households",
    globalOrder: 21,
    storyId: "olympians",
    stepId: "olympians-leto-twins",
    sourceStepIds: ["olympians-leto-twins", "olympians-hera-house-athena"],
    sourceIds: ["theogony-918-929-olympian-offspring", "homeric-hymn-3-1-15-apollo-olympus", "homeric-hymn-28-1-15-athena-birth"],
    tracks: {
      en: track("olympian-children-and-households", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 41,
        text: "After kingship settles, the household on Mount Olympus gains clearer functions. The children of Zeus and the house of Hera make the divine world operate like a court. Leto bears Apollo and Artemis; Hera's children bring youth, war, childbirth, and craft; Athena's armed birth brings wisdom and strategy into the centre of rule. The gods are no longer only a succession list.",
      }),
      'zh-Hant-mandarin': track("olympian-children-and-households", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 48,
        text: "王權穩定後，奧林匹斯山上的家族開始有更清楚的職能輪廓。勒托生下阿波羅與阿耳忒彌斯，把神諭、疫病、弓箭與狩獵帶進奧林匹斯。赫拉生下赫柏、阿瑞斯、厄勒梯亞與赫淮斯托斯，讓青春、戰爭、生產與工藝有了家庭位置；雅典娜的披甲誕生則把智慧與戰略接到王權核心。眾神不再只是承繼名單，而變成日後英雄故事會反覆呼喚、衝突與求助的角色網絡。",
      }),
    },
  },
  {
    sceneId: "olympians-olympians-hearth-centre",
    globalOrder: 22,
    storyId: "olympians",
    stepId: "olympians-hearth-centre",
    sourceStepIds: ["olympians-hearth-centre"],
    sourceIds: ["homeric-hymn-5-1-32-hestia-honour"],
    tracks: {
      en: track("olympians-olympians-hearth-centre", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 45,
        text: "Within the divine household, some powers are defined by refusing to be drawn into marriage plots. Hestia's place is built from refusal and stability. The Hymn to Aphrodite places Athena, Artemis, and Hestia outside Aphrodite's erotic power. Poseidon and Apollo seek Hestia, but she vows before Zeus, who grants her the central place in home and temple. The Olympian Order therefore has more than conquest and marriage.",
      }),
      'zh-Hant-mandarin': track("olympians-olympians-hearth-centre", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 55,
        text: "在眾神家族中，也有力量選擇不被婚配故事牽走。赫斯提亞的位置，正是從拒絕與安定之間建立出來。《致阿佛洛狄忒頌歌》把雅典娜、阿耳忒彌斯與赫斯提亞置於阿佛洛狄忒的愛慾權力之外。波塞冬與阿波羅都向赫斯提亞求婚，但她向宙斯立誓保持處女身份；宙斯因此給她家宅與神殿中央的位置，並讓她享有祭品首份。奧林匹斯秩序因此不單有征服與婚姻，也有一個固定中心：爐火讓每個家與祭壇都能被重新聚合。",
      }),
    },
  },
  {
    sceneId: "prometheus-fire-and-pandora",
    globalOrder: 23,
    storyId: "olympians",
    stepId: "olympians-prometheus-human-order",
    sourceStepIds: ["olympians-prometheus-human-order"],
    sourceIds: ["theogony-521-534-prometheus-bound", "theogony-535-560-mecone-sacrifice", "theogony-561-616-fire-first-woman", "works-days-42-58-fire-response", "works-days-59-82-pandora-gifts-name", "works-days-83-105-epimetheus-jar"],
    tracks: {
      en: track("prometheus-fire-and-pandora", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 43,
        text: "After Zeus' kingship is secured, the sharing of sacrifice, fire, and mortal hardship is still unsettled. Prometheus draws humanity into the new divine-human order. At Mecone he arranges the sacrificial division, leading Zeus to withhold fire; then he steals fire for mortals. Zeus orders the making of Pandora, and Epimetheus accepts the gift. Fire makes human life possible, but cost arrives with it.",
      }),
      'zh-Hant-mandarin': track("prometheus-fire-and-pandora", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 52,
        text: "宙斯的王權確立後，神與人如何分享祭物、火種與生活代價仍未穩定。普羅米修斯於是把人類拉進新的神人秩序。在墨科涅祭肉分配，普羅米修斯安排兩份祭肉，引出宙斯扣起火種；他再以茴香莖盜取天火給人類。宙斯命赫淮斯托斯、雅典娜與赫耳墨斯參與塑造潘多拉，又讓厄庇墨透斯接受這份禮物。火讓人類能生活，代價也同時落下。祭禮、技術、懲罰與希望在這裡被鎖在同一段神人關係裏。",
      }),
    },
  },
  {
    sceneId: "olympians-olympians-order-in-practice",
    globalOrder: 24,
    storyId: "olympians",
    stepId: "olympians-order-in-practice",
    sourceStepIds: ["olympians-order-in-practice"],
    sourceIds: ["iliad-1-493-611-olympian-counsel", "odyssey-1-26-95-divine-council", "odyssey-5-1-42-council-command"],
    tracks: {
      en: track("olympians-olympians-order-in-practice", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 46,
        text: "Divine order is not a permanently calm heavenly chart. Epic shows the gods working through petitions, quarrels, and commands. Thetis privately petitions Zeus and provokes Hera's anger; Hephaestus mediates. Athena argues in the Olympian Council for Odysseus' return, and Zeus sends Hermes to carry the decree. Mount Olympus is therefore both the centre of rule and the place where divine will is negotiated, delayed, and enacted.",
      }),
      'zh-Hant-mandarin': track("olympians-olympians-order-in-practice", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 47,
        text: "眾神的秩序不是永遠平靜的天上行政表。史詩讓我們看見神明如何在請願、爭執與傳令中實際運作。忒提斯私下向宙斯請求暫助對岸一方，觸發赫拉的不滿；赫淮斯托斯以宮廷式調停避免爭執失控。另一邊，雅典娜在奧林匹斯議會推動奧德修斯的歸鄉，宙斯再委派赫耳墨斯傳達決議。因此，奧林匹斯山既是王權中心，也是神意被協商、延遲和執行的地方。",
      }),
    },
  },
  {
    sceneId: "underworld-enters-olympian-order",
    globalOrder: 25,
    storyId: "underworld",
    stepId: "underworld-tartarus-distinction",
    sourceStepIds: ["underworld-tartarus-distinction", "underworld-persephone-divided-year"],
    sourceIds: ["theogony-736-806-tartarus-gates", "theogony-807-819-titans-guards", "homeric-hymn-2-1-39-persephone-taken", "homeric-hymn-2-305-356-famine-hermes", "homeric-hymn-2-357-433-pomegranate-return"],
    tracks: {
      en: track("underworld-enters-olympian-order", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 43,
        text: "After Olympus is established, the world below must also be understood. Tartarus is not the whole Underworld but a deep place of confinement and punishment. The Titans are confined in Tartarus; Styx belongs to the same deep-world imagination. Hades rules the dead, Persephone becomes queen below, and Demeter's loss affects the seasons. The lower world is therefore not simply a frightening backdrop.",
      }),
      'zh-Hant-mandarin': track("underworld-enters-olympian-order", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 50,
        text: "奧林匹斯建立後，世界下方也需要被重新理解。塔耳塔羅斯不是整個冥界，而是深處囚禁與懲罰的一部分。泰坦神族被關在塔耳塔羅斯；斯堤克斯連到同一深界想像。哈得斯統治亡者之境，珀耳塞福涅被帶入地下後成為冥后；得墨忒耳的失女之痛令地上季節受影響。地下世界因此不再只是恐怖背景，而是奧林匹斯秩序的一面：死亡、婚姻、回歸和不可完全回歸都在其中運作。",
      }),
    },
  },
  {
    sceneId: "argonauts-one-sandal-command",
    globalOrder: 26,
    storyId: "argonauts",
    stepId: "argonauts-01",
    sourceStepIds: ["argonauts-01", "argonauts-02"],
    sourceIds: ["apollonius-argonautica-1-1-22-command", "pindar-pythian-4-125-299-jason", "apollonius-argonautica-1-23-233-catalogue", "apollonius-argonautica-1-306-558-argo-departure"],
    tracks: {
      en: track("argonauts-one-sandal-command", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 37,
        text: "The heroic age opens with Jason not as a lone glory seeker but as the one-sandalled man returning to Iolcus, fulfilling the sign Pelias fears. Pelias turns the Golden Fleece into an almost impossible command. The voyage is therefore not one man fetching an object. It is a generation of heroes temporarily woven into one ship and one story.",
      }),
      'zh-Hant-mandarin': track("argonauts-one-sandal-command", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 50,
        text: "英雄時代打開時，伊阿宋並不是主動尋找榮耀的孤膽英雄。他以「單鞋人」的姿態回到伊奧爾科斯，踩中珀利阿斯害怕的預言。珀利阿斯把金羊毛設成幾乎不能完成的任務，希望命令本身能把威脅送走。赫拉的關注、雅典娜協助造船、造船者阿爾戈斯建成阿爾戈號，都把私人王權焦慮變成阿爾戈英雄遠征。遠征因此不是一個人去取寶物，而是一代英雄被同一艘船暫時編成共同故事。",
      }),
    },
  },
  {
    sceneId: "argonauts-lemnos-and-cyzicus",
    globalOrder: 27,
    storyId: "argonauts",
    stepId: "argonauts-03",
    sourceStepIds: ["argonauts-03", "argonauts-04"],
    sourceIds: ["apollonius-argonautica-1-609-909-lemnos", "apollonius-argonautica-1-910-1152-cyzicus"],
    tracks: {
      en: track("argonauts-lemnos-and-cyzicus", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 44,
        text: "After the Argo leaves home, the Argonaut Expedition quickly proves that the sea route is not a straight errand but a sequence of landings that delay, tempt, and wound the crew. On Lemnos, Hypsipyle receives Jason and the heroes linger until Heracles urges departure. The voyage becomes more than an adventure list. The heroes can move onward, but each shore may leave attachment, mistake, and grief behind.",
      }),
      'zh-Hant-mandarin': track("argonauts-lemnos-and-cyzicus", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 48,
        text: "阿爾戈號離開故鄉後，阿爾戈英雄遠征很快證明海路不是直線任務，而是一連串會拖慢、誘惑與傷害隊伍的停泊。在利姆諾斯，海普西皮勒接待伊阿宋，英雄們停留太久，直到赫拉克勒斯催促眾人重新出航。到庫濟科斯一帶，款待與友誼又被夜色扭曲：逆風把船送回，雙方不相認而交戰，伊阿宋殺死曾經接待自己的王。這兩站把遠征從冒險名單變成有代價的航程。",
      }),
    },
  },
  {
    sceneId: "argonauts-hylas-and-amycus",
    globalOrder: 28,
    storyId: "argonauts",
    stepId: "argonauts-05",
    sourceStepIds: ["argonauts-05", "argonauts-06"],
    sourceIds: ["apollonius-argonautica-1-1153-1328-hylas", "apollodorus-library-1-9-16-19-outward", "apollonius-argonautica-2-1-163-amycus"],
    tracks: {
      en: track("argonauts-hylas-and-amycus", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 42,
        text: "Progress does not mean the crew remains whole. Hylas vanishes while fetching water; Heracles stays behind to search, and the Argo sails before the absence is fully grasped. Then Amycus blocks the route with boxing, and Polydeuces opens the way by defeating him. The scene holds loss and replacement together: the voyage does not depend on one hero, but every separation leaves a gap.",
      }),
      'zh-Hant-mandarin': track("argonauts-hylas-and-amycus", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 45,
        text: "前進不代表隊伍完整。赫拉斯取水時失蹤，赫拉克勒斯留岸尋找；阿爾戈號在沒有即時察覺的情況下出航，伊阿宋的遠征隊從此少了最強大的英雄之一。接着阿密科斯以拳鬥攔路，波呂丟刻斯接下挑戰並擊敗他，讓隊伍靠另一種本領打開航道。這段航程同時呈現損失與替補：遠征不靠單一英雄支撐，卻必須承受每一次分離留下的缺口。",
      }),
    },
  },
  {
    sceneId: "argonauts-phineus-and-clashing-rocks",
    globalOrder: 29,
    storyId: "argonauts",
    stepId: "argonauts-07",
    sourceStepIds: ["argonauts-07", "argonauts-08"],
    sourceIds: ["apollonius-argonautica-2-178-447-phineus-harpies", "apollonius-argonautica-2-528-647-symplegades"],
    tracks: {
      en: track("argonauts-phineus-and-clashing-rocks", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 42,
        text: "When the Argonauts reach Phineus, the voyage truly approaches a mythic boundary. The route can be known, but knowledge itself comes with torment. Phineus suffers under the Harpies until Zetes and Calais drive them off. He then explains the Clashing Rocks, and the Argo follows the dove's timing, with Athena's aid, through the closing passage. The voyage crosses beyond ordinary sailing.",
      }),
      'zh-Hant-mandarin': track("argonauts-phineus-and-clashing-rocks", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 53,
        text: "阿爾戈英雄來到菲紐斯面前時，遠征第一次真正接近神話邊界。前路可以被知道，但知識本身也帶着折磨。菲紐斯受哈耳庇厄污染食物之苦；仄忒斯與卡拉伊斯以有翼速度追逐驅離，換得先知說出撞擊岩的航路。阿爾戈號依照鴿子的試探與時機闖過岩門；雅典娜再推了一把，船尾也穿過合攏的岩石。從此，遠征越過普通航海的界線。英雄們抵達目的地，不靠蠻力，而靠援助、預言與準確行動彼此配合。",
      }),
    },
  },
  {
    sceneId: "argonauts-argonauts-09",
    globalOrder: 30,
    storyId: "argonauts",
    stepId: "argonauts-09",
    sourceStepIds: ["argonauts-09"],
    sourceIds: ["apollonius-argonautica-2-1030-1285-colchis", "apollonius-argonautica-3-210-438-aeetes-test"],
    tracks: {
      en: track("argonauts-argonauts-09", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 46,
        text: "When Jason reaches Colchis, Pelias' command is no longer a distant pressure; it stands before the kingship of Aeetes. The Golden Fleece was left by Phrixus in land tied to Helios' line. Aeetes suspects the strangers of aiming at his rule and turns the request into the deadly trial of yoking the fire-breathing bulls. The voyage becomes a court crisis. Jason now needs more than courage; he needs help from within Colchis.",
      }),
      'zh-Hant-mandarin': track("argonauts-argonauts-09", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 52,
        text: "伊阿宋終於抵達科爾喀斯時，珀利阿斯的命令不再只是遠方壓力，而被放到埃厄忒斯的王權面前。金羊毛原由佛里克索斯留下，懸在與赫利俄斯家系相連的土地上。伊阿宋提出要求後，埃厄忒斯懷疑這群外來者真正想奪取王位，於是把任務改寫成駕馭噴火公牛的致命試煉。阿爾戈英雄遠征到此由航海故事變成宮廷危機。下一步，伊阿宋需要的不再只是勇氣，還需要科爾喀斯內部有人願意幫他。",
      }),
    },
  },
  {
    sceneId: "argonauts-argonauts-10",
    globalOrder: 31,
    storyId: "argonauts",
    stepId: "argonauts-10",
    sourceStepIds: ["argonauts-10"],
    sourceIds: ["apollonius-argonautica-3-6-110-divine-plan", "apollonius-argonautica-3-471-1162-medea-help"],
    tracks: {
      en: track("argonauts-argonauts-10", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 46,
        text: "Facing Aeetes' terms, Jason cannot solve the crisis by strength alone. The gods turn the story toward Medea's inner conflict. Hera and Athena seek a solution, Aphrodite calls Eros, and desire strikes Medea. Yet the scene is not simply magical control: Medea still weighs fear, family, her service to Hecate, and escape before giving Jason the charm and tactics. From here, the expedition is no longer only a Greek heroic story.",
      }),
      'zh-Hant-mandarin': track("argonauts-argonauts-10", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 50,
        text: "面對埃厄忒斯的條件，伊阿宋的力量不足以獨自解局。神明於是把故事推向美狄亞的內心。赫拉與雅典娜尋找解法，阿佛洛狄忒召來厄洛斯，以箭使美狄亞對伊阿宋產生強烈渴望。可是這段故事不能只讀成魔法控制：美狄亞仍在恐懼、父家、赫卡忒的祭司身份與逃離可能之間掙扎，最後親自交出藥膏與策略，教伊阿宋如何面對試煉。從這裏開始，遠征不再只是希臘英雄的故事。",
      }),
    },
  },
  {
    sceneId: "argonauts-argonauts-11",
    globalOrder: 32,
    storyId: "argonauts",
    stepId: "argonauts-11",
    sourceStepIds: ["argonauts-11"],
    sourceIds: ["apollonius-argonautica-3-1163-1407-yoking-bulls", "apollonius-argonautica-4-6-211-fleece"],
    tracks: {
      en: track("argonauts-argonauts-11", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 39,
        text: "Medea's help carries Jason into the trial imposed by Aeetes. Jason survives the fire-breathing bulls with the charm, handles the dragon-born warriors, and then, when Aeetes withholds the Fleece, follows Medea to the sleepless dragon. She subdues it, and Jason takes the Golden Fleece from the oak. The task is complete, but peaceful closure is impossible.",
      }),
      'zh-Hant-mandarin': track("argonauts-argonauts-11", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 42,
        text: "美狄亞的幫助把伊阿宋帶進埃厄忒斯設下的試煉。真正的危險不是單一怪物，而是一連串不能出錯的條件。伊阿宋靠藥膏抵住噴火公牛，完成駕馭噴火公牛，又依照美狄亞指示處理龍牙武士。可是埃厄忒斯仍拒絕交出金羊毛。任務完成的同時，和平結束已經不可能。金羊毛離開科爾喀斯，也把美狄亞推離父家。",
      }),
    },
  },
  {
    sceneId: "argonauts-argonauts-12",
    globalOrder: 33,
    storyId: "argonauts",
    stepId: "argonauts-12",
    sourceStepIds: ["argonauts-12"],
    sourceIds: ["apollonius-argonautica-4-294-551-absyrtus", "apollonius-argonautica-4-552-981-circe-sirens", "apollonius-argonautica-4-982-1169-phaeacians", "apollonius-argonautica-4-1170-1637-libya-triton", "apollonius-argonautica-4-1638-1781-talos-return"],
    tracks: {
      en: track("argonauts-argonauts-12", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 46,
        text: "After the Golden Fleece is taken, the Argo's return is not a triumphal sail but a flight under pollution. Pursuit and guilt catch Jason and Medea quickly. Apsyrtus pursues and is killed in an ambush; Zeus sends a storm over the murder, forcing purification by Circe. The Argonaut Expedition closes with victory and shadow together: the Fleece is brought back, but the crew has been changed by departure, killing, and purification.",
      }),
      'zh-Hant-mandarin': track("argonauts-argonauts-12", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 52,
        text: "取走金羊毛之後，阿爾戈號的返航不再是凱旋，而是帶着污染逃離。阿布敘爾托斯率隊追擊，卻被誘入陷阱並由伊阿宋殺死；宙斯因殺人污染降下風暴，迫使眾人尋求喀耳刻淨化。之後，塞壬的歌聲被奧菲斯壓過，斯庫拉與卡律布狄斯成為返航威脅，塔洛斯又拒絕登岸並被美狄亞擊倒。阿爾戈英雄遠征因此以勝利和陰影一起收束：金羊毛帶回來了，但船上每個人都已被離家、殺戮與淨化改變。",
      }),
    },
  },
  {
    sceneId: "underworld-underworld-do-not-look-back",
    globalOrder: 34,
    storyId: "underworld",
    stepId: "underworld-do-not-look-back",
    sourceStepIds: ["underworld-do-not-look-back"],
    sourceIds: ["apollodorus-library-1-3-2-orpheus-eurydice"],
    tracks: {
      en: track("underworld-underworld-do-not-look-back", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 41,
        text: "The heroic age also includes stories driven not by victory but by loss. The later Library tells the episode briefly but clearly: Orpheus descends, Persephone permits Eurydice to return under a condition, and Orpheus looks back before leaving. The condition breaks, and Eurydice remains among the dead again. This story does not make the Underworld a place heroes can simply conquer.",
      }),
      'zh-Hant-mandarin': track("underworld-underworld-do-not-look-back", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 50,
        text: "英雄時代也有故事不是靠勝利推進，而是靠失去。奧菲斯因歐律狄刻死去而走向冥界。《書庫》把這段說得很短，卻留下清楚的悲劇形狀：奧菲斯下冥府，用自己的歌與請求觸及死亡邊界；珀耳塞福涅准許歐律狄刻有條件返回，但規定離開前不得回望。這段故事不是把冥界寫成可被英雄征服的地方，而是留下一道限制：有些門即使短暫打開，也不能被人的不安完全穿過。",
      }),
    },
  },
  {
    sceneId: "heracles-lion-and-hydra",
    globalOrder: 35,
    storyId: "heracles",
    stepId: "heracles-labour-01",
    sourceStepIds: ["heracles-labour-01", "heracles-labour-02"],
    sourceIds: ["iliad-19-95-133-eurystheus-rule", "apollodorus-library-2-4-12-heracles-command", "apollodorus-library-2-5-1-nemean-lion", "apollodorus-library-2-5-2-lernaean-hydra"],
    tracks: {
      en: track("heracles-lion-and-hydra", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 39,
        text: "The Labours begin by placing Heracles under Eurystheus' command. This is not free adventure but service measured by authority. The Nemean Lion cannot be harmed by ordinary weapons, so Heracles kills it at close range and takes the skin. The opening tasks set the pattern: the hero gains power and trophies, but the counting of achievement remains contested.",
      }),
      'zh-Hant-mandarin': track("heracles-lion-and-hydra", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 50,
        text: "赫拉克勒斯的試煉開場，先把他放到歐律斯透斯的命令之下。尼米亞獅子以普通武器難傷，赫拉克勒斯只能近身扼殺，並把獅皮變成日後身份的一部分。接着，勒拿九頭蛇把戰鬥變成更難的再生問題；伊奧勞斯用火阻止蛇首重生，赫拉克勒斯取下毒液，但歐律斯透斯仍以有人協助為由拒絕計算。最初兩項試煉定下規則：英雄會取得戰利品與力量，也會被制度挑剔、否認和重新安排。",
      }),
    },
  },
  {
    sceneId: "heracles-sacred-hind-and-boar",
    globalOrder: 36,
    storyId: "heracles",
    stepId: "heracles-labour-03",
    sourceStepIds: ["heracles-labour-03", "heracles-labour-04"],
    sourceIds: ["apollodorus-library-2-5-3-ceryneian-hind", "apollodorus-library-2-5-4-erymanthian-boar"],
    tracks: {
      en: track("heracles-sacred-hind-and-boar", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 43,
        text: "After monsters, Heracles' labours turn toward capture. Eurystheus does not simply demand killing; he requires danger to be brought back alive. The Ceryneian Hind belongs to Artemis, so the task touches sacred ownership. Heracles pursues it and pleads necessity when challenged. Strength is not the only answer here. The hero must work between divine boundaries, royal command, and living resistance.",
      }),
      'zh-Hant-mandarin': track("heracles-sacred-hind-and-boar", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 50,
        text: "怪物之後，赫拉克勒斯的勞役轉向活捉。歐律斯透斯要他帶回的不是屍體，而是仍然活着的危險。刻律涅亞牝鹿屬於阿耳忒彌斯，任務因此碰到神聖所有權；赫拉克勒斯追逐牝鹿，卻不能把成功變成隨意傷害。當阿耳忒彌斯質問時，他把必要性歸向王命，才把牝鹿帶走。力量不是唯一答案。英雄必須同時面對神的界線、王的要求與活物的抵抗，找到一種能被承認的完成方式。",
      }),
    },
  },
  {
    sceneId: "heracles-stables-and-birds",
    globalOrder: 37,
    storyId: "heracles",
    stepId: "heracles-labour-05",
    sourceStepIds: ["heracles-labour-05", "heracles-labour-06"],
    sourceIds: ["apollodorus-library-2-5-5-augean-stables", "apollodorus-library-2-5-6-stymphalian-birds"],
    tracks: {
      en: track("heracles-stables-and-birds", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 46,
        text: "The middle labours move Heracles beyond simple combat into cleansing, tools, and accounting. Heracles cleans the Augean Stables in one day by diverting rivers, but Eurystheus refuses to count the labour because of the promised reward. Against the Stymphalian Birds, Athena's bronze clappers change the method of attack. The issue is not simply whether the hero can perform the deed, but who has authority to decide that it counts.",
      }),
      'zh-Hant-mandarin': track("heracles-stables-and-birds", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 51,
        text: "中段勞役把赫拉克勒斯推離單純搏鬥，轉向清除、工具與計算。歐律斯透斯的命令開始考驗完成方式本身。奧革阿斯的牛舍積滿污穢，赫拉克勒斯引河一日洗淨，卻因事前談及報酬而被歐律斯透斯拒絕計算。到了斯廷法利斯怪鳥，雅典娜給予青銅響器，赫拉克勒斯以巨聲驚起怪鳥再射殺牠們。因此，試煉不單問英雄能不能完成，也問誰有權判定「完成」；力量、技巧與認可在這裏分開。",
      }),
    },
  },
  {
    sceneId: "heracles-bull-mares-and-belt",
    globalOrder: 38,
    storyId: "heracles",
    stepId: "heracles-labour-07",
    sourceStepIds: ["heracles-labour-07", "heracles-labour-08", "heracles-labour-09"],
    sourceIds: ["apollodorus-library-2-5-7-cretan-bull", "apollodorus-library-2-5-8-mares-diomedes", "apollodorus-library-2-5-9-hippolyta-belt"],
    tracks: {
      en: track("heracles-bull-mares-and-belt", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 41,
        text: "The next labours carry Heracles toward political and social edges. Eurystheus' commands now touch other rulers' domains. Minos permits the capture of the Cretan Bull without truly helping. The Mares of Diomedes mark a brutal Thracian power and cost Abderus his life. The farther the hero travels, the easier it is for misunderstanding and divine hostility to turn a task violent.",
      }),
      'zh-Hant-mandarin': track("heracles-bull-mares-and-belt", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 54,
        text: "接下來的勞役把赫拉克勒斯帶到更遠的政治與人群邊界。歐律斯透斯的命令不再只針對怪物，也觸碰其他統治者的領域。米諾斯准許他處理克里特公牛，卻不提供真正協助；狄俄墨得斯的牝馬則以食人恐怖標示色雷斯的狄俄墨得斯的殘暴，阿布德羅斯也在這段中付出代價。到希波呂忒與亞馬遜人面前，腰帶原本可能和平交出，卻因赫拉散布假消息變成衝突。英雄越走越遠，誤會與神意就越容易把任務推向暴力。",
      }),
    },
  },
  {
    sceneId: "heracles-cattle-and-apples",
    globalOrder: 39,
    storyId: "heracles",
    stepId: "heracles-labour-10",
    sourceStepIds: ["heracles-labour-10", "heracles-labour-11"],
    sourceIds: ["apollodorus-library-2-5-10-geryon-cattle", "apollodorus-library-2-5-11-hesperides-apples", "diodorus-library-4-25-1-26-4-cerberus-apples"],
    tracks: {
      en: track("heracles-cattle-and-apples", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 46,
        text: "The later labours push Heracles to the world's edge. Helios lends the cup for crossing Ocean; Heracles drives away the cattle despite Hera's disruption. For the apples, Prometheus advises using Atlas, so Heracles briefly takes up the sky and then tricks Atlas into bearing it again. The labours become cosmic tests of endurance and intelligence. The hero does not simply defeat enemies; he briefly carries the weight of order.",
      }),
      'zh-Hant-mandarin': track("heracles-cattle-and-apples", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 53,
        text: "後段勞役把赫拉克勒斯推到世界邊界。赫利俄斯借出越洋金杯，使赫拉克勒斯抵達牛群所在；他射殺守主、趕走革律翁的牛群，又要面對赫拉以牛虻驅散牛群。尋找赫斯珀里得斯花園時，普羅米修斯建議讓阿特拉斯代取金蘋果；赫拉克勒斯短暫承受天空，再以話術把重擔交還，雅典娜最後把神物送回。這段故事把勞役推成宇宙尺度的智力與耐力考驗：英雄不單征服敵人，也短暫承接世界秩序的重量。",
      }),
    },
  },
  {
    sceneId: "heracles-cerberus-and-return",
    globalOrder: 40,
    storyId: "heracles",
    stepId: "heracles-labour-12",
    sourceStepIds: ["heracles-labour-12", "underworld-hound-bare-handed"],
    sourceIds: ["apollodorus-library-2-5-12-heracles-cerberus", "euripides-heracles-1-25-cerberus-last", "euripides-heracles-815-1015-madness-after-return"],
    tracks: {
      en: track("heracles-cerberus-and-return", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 37,
        text: "The final labour requires Heracles to enter the Underworld. Eurystheus demands Cerberus, the guardian of the threshold of death. Heracles descends through Taenarum toward the underworld route. Hades permits the taking only if no weapons are used, and Persephone and Hermes keep the crossing within divine oversight. Cerberus is returned below.",
      }),
      'zh-Hant-mandarin': track("heracles-cerberus-and-return", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 52,
        text: "最後一項勞役要求赫拉克勒斯走進冥界。歐律斯透斯要他帶回刻耳柏洛斯，等於要求活人觸碰死亡秩序的守門者。赫拉克勒斯下冥府從泰納隆展開，路線連到阿刻戎河與地下世界。哈得斯允許他帶走冥犬，但條件是不得用武器；珀耳塞福涅與赫耳墨斯也把這次越界放進神明監看的秩序裏。刻耳柏洛斯最後被送回冥界。勞役以越界又復位收束：英雄可以進入死亡領域，卻不能把冥界秩序永久帶走。",
      }),
    },
  },
  {
    sceneId: "trojan-war-judgement-of-paris",
    globalOrder: 41,
    storyId: "trojan-war",
    stepId: "judgement-of-paris",
    sourceStepIds: ["judgement-of-paris"],
    sourceIds: ["apollodorus-epitome-3-1-2"],
    tracks: {
      en: track("trojan-war-judgement-of-paris", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 40,
        text: "The Trojan age begins not on a battlefield, but with a choice placed in Paris' hands. Hera promises rule, Athena promises victory and Aphrodite promises Helen. Paris chooses Aphrodite and accepts her promise. By the time Helen leaves Sparta, this choice is no longer only a contest among goddesses. It has begun to move a generation of heroes toward disaster beneath Troy.",
      }),
      'zh-Hant-mandarin': track("trojan-war-judgement-of-paris", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 52,
        text: "特洛伊時代的開端看似不是戰場，而是一次由神明交到人手上的選擇。三位女神爭奪「最美者」之名，宙斯不親自判斷，命赫耳墨斯帶赫拉、雅典娜與阿佛洛狄忒去見伊達山上的帕里斯。赫拉許下王權，雅典娜許下戰勝，阿佛洛狄忒許下海倫。帕里斯選擇阿佛洛狄忒，也接受了海倫的承諾。到海倫離開斯巴達時，這個選擇不再只是女神之間的爭勝，而是讓英雄世代一步步走向特洛伊城下的災難。",
      }),
    },
  },
  {
    sceneId: "trojan-war-helen-leaves-sparta",
    globalOrder: 42,
    storyId: "trojan-war",
    stepId: "helen-leaves-sparta",
    sourceStepIds: ["helen-leaves-sparta"],
    sourceIds: ["apollodorus-epitome-3-3-5"],
    tracks: {
      en: track("trojan-war-helen-leaves-sparta", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 40,
        text: "Paris' choice quickly leaves divine rivalry and enters a human household. Paris departs Sparta with Helen after being received as a guest, and Aphrodite's promise becomes action. Different traditions remain, but this route focuses on the departure that unbalances marriage, guest-friendship, and divine promise. Once Helen reaches Troy, the matter is no longer private.",
      }),
      'zh-Hant-mandarin': track("trojan-war-helen-leaves-sparta", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 53,
        text: "帕里斯的選擇很快離開神明競逐，落到人間家宅。海倫與墨涅拉俄斯的婚姻，成為特洛伊故事第一個被撕開的位置。帕里斯受款待後與海倫離開斯巴達；阿佛洛狄忒的承諾在這裏變成行動，而特洛伊成為目的地。古代傳統並不一致：有的讓海倫真身前往特洛伊，有的保存幻影海倫的版本；此處的重點是離開本身如何讓婚姻、客禮與神明承諾一起失衡。當海倫抵達目的地，事件已不再是兩個人的私事。",
      }),
    },
  },
  {
    sceneId: "trojan-war-achaean-coalition",
    globalOrder: 43,
    storyId: "trojan-war",
    stepId: "achaean-coalition",
    sourceStepIds: ["achaean-coalition"],
    sourceIds: ["apollodorus-epitome-3-6-7", "apollodorus-epitome-3-11-16"],
    tracks: {
      en: track("trojan-war-achaean-coalition", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 42,
        text: "After the marriage breaks, Menelaus cannot restore the situation alone. Agamemnon becomes commander, and Odysseus, Nestor, Diomedes, Ajax, Achilles, and others are drawn into the expedition. The crisis grows from one husband's grievance into a coalition action. The key change is scale: a private rupture becomes public war, and a whole heroic generation is pulled toward one expedition.",
      }),
      'zh-Hant-mandarin': track("trojan-war-achaean-coalition", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 53,
        text: "婚姻破裂後，墨涅拉俄斯不能獨自把事情帶回原狀。阿伽門農成為聯軍統帥，奧德修斯、涅斯托耳、狄俄墨得斯、大埃阿斯與阿基里斯等人加入共同遠征。受辱丈夫的請求，逐漸擴成希臘聯軍的行動；每個英雄都帶着自己的名望、義務與未來悲劇登場。私人破裂變成公共戰爭，整個英雄世代也被同一個遠征目標牽引。",
      }),
    },
  },
  {
    sceneId: "trojan-war-aulis-and-crossing",
    globalOrder: 44,
    storyId: "trojan-war",
    stepId: "aulis-and-crossing",
    sourceStepIds: ["aulis-and-crossing"],
    sourceIds: ["apollodorus-epitome-3-19-22"],
    tracks: {
      en: track("trojan-war-aulis-and-crossing", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 46,
        text: "After the coalition forms, the Trojan War still cannot sail at once. Artemis becalms the fleet, placing Agamemnon's household at the centre of the coalition's fate. Iphigenia is brought under a marriage pretext by Odysseus; another ancient version preserves the substitution with a deer. The expedition is not a clean act of justice from the beginning. When the ships leave Aulis, the desire for victory already carries family sacrifice.",
      }),
      'zh-Hant-mandarin': track("trojan-war-aulis-and-crossing", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 44,
        text: "希臘聯軍組成後，特洛伊戰爭仍未能立刻啟航。奧利斯成為出發前的卡口，軍隊在海岸等待風，也等待代價被說出。阿耳忒彌斯使艦隊無風受阻，阿伽門農的家庭因此被放到聯軍命運中央。伊菲革涅亞被奧德修斯以婚約名義帶來；另一個古代版本保存替鹿說法，讓她在啟航代價中被神明轉移。遠征從一開始就不是乾淨的正義行動。",
      }),
    },
  },
  {
    sceneId: "trojan-war-plague-and-wrath",
    globalOrder: 45,
    storyId: "trojan-war",
    stepId: "plague-and-wrath",
    sourceStepIds: ["plague-and-wrath"],
    sourceIds: ["iliad-1-plague-quarrel", "iliad-9-embassy"],
    tracks: {
      en: track("trojan-war-plague-and-wrath", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 44,
        text: "Inside the Greek camp, order collapses before the decisive fighting resumes. Apollo's Plague places divine anger among the Greeks. Chryses' ransom request is rejected by Agamemnon, and Apollo sends disease. When Agamemnon yields, he seizes Briseis, causing Achilles to withdraw. The greatest warrior is pulled from the field. The coalition remains, but victory becomes unstable and costly without Achilles.",
      }),
      'zh-Hant-mandarin': track("trojan-war-plague-and-wrath", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 44,
        text: "希臘軍營內部秩序崩裂時，《伊利亞特》的危機才真正開始。阿波羅降疫把神怒直接放進希臘營地。克律塞斯的贖人請求被阿伽門農拒絕，阿波羅因而降下疫病。當阿伽門農被迫讓步，他又奪取布里塞伊斯，令阿基里斯憤怒撤出戰鬥。這段故事把最大英雄從戰場抽走。希臘聯軍仍在，但失去阿基里斯後，勝利開始變得昂貴而不穩。",
      }),
    },
  },
  {
    sceneId: "trojan-war-helen-on-walls",
    globalOrder: 46,
    storyId: "trojan-war",
    stepId: "helen-on-walls",
    sourceStepIds: ["helen-on-walls"],
    sourceIds: ["iliad-3-duel-helen"],
    tracks: {
      en: track("trojan-war-helen-on-walls", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 46,
        text: "On the walls, Helen becomes the person who can name both sides. She is being fought over, yet she also gives the enemy a voice and a face. Priam asks her to name the Greek leaders, and then the duel between Paris and Menelaus seems to reduce the war back to two men. Aphrodite rescues Paris and compels Helen back toward him. The war cannot be closed by one duel. Helen sees the names and sees that she cannot stop the story.",
      }),
      'zh-Hant-mandarin': track("trojan-war-helen-on-walls", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 45,
        text: "戰爭進行到城牆之上，海倫重新成為理解雙方的人。她身處被爭奪的位置，也在城中說出敵人的名字。普里阿摩斯請海倫指認希臘將領，使戰場人物暫時變成可被講述的面孔。隨後，帕里斯與墨涅拉俄斯的決鬥似乎可以讓戰爭縮回兩個男人之間；可是阿佛洛狄忒救走敗勢中的帕里斯，又把海倫推回帕里斯身邊。戰爭因此無法被一次決鬥關閉。",
      }),
    },
  },
  {
    sceneId: "trojan-war-gods-on-battlefield",
    globalOrder: 47,
    storyId: "trojan-war",
    stepId: "gods-on-battlefield",
    sourceStepIds: ["gods-on-battlefield"],
    sourceIds: ["iliad-5-diomedes-gods"],
    tracks: {
      en: track("trojan-war-gods-on-battlefield", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 39,
        text: "When human duels cannot settle the war, the gods step more directly onto the battlefield. Diomedes' episode lets a mortal briefly see that gods can be wounded. Athena gives him sight and force. He wounds Aphrodite as she rescues Aeneas, then wounds Ares with Athena's help, while Apollo takes over Aeneas' protection. The war becomes more than hero against hero.",
      }),
      'zh-Hant-mandarin': track("trojan-war-gods-on-battlefield", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 48,
        text: "當戰爭不能由人間決鬥解決，眾神也更直接走上戰場。狄俄墨得斯的章節讓凡人短暫看見神明也會受傷。雅典娜賦予狄俄墨得斯辨認神的視力與戰力，使他能分辨誰在戰場上真正介入。他先刺傷前來救援兒子埃涅阿斯的阿佛洛狄忒，後來又在雅典娜協助下刺傷阿瑞斯；阿波羅則接手保護埃涅阿斯，阻止死亡過早帶走日後倖存者。這段場景使戰爭不再只是英雄對英雄。",
      }),
    },
  },
  {
    sceneId: "trojan-war-hector-and-city",
    globalOrder: 48,
    storyId: "trojan-war",
    stepId: "hector-and-city",
    sourceStepIds: ["hector-and-city"],
    sourceIds: ["iliad-6-hector-andromache"],
    tracks: {
      en: track("trojan-war-hector-and-city", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 44,
        text: "Amid battle, Hector returns to Troy, so the city becomes more than a besieged target. It becomes a home where people wait and fear. He meets Priam, Hecuba, Paris, Helen, and Andromache. Each encounter brings the cost closer: he is the defender of the city, but also son, brother, and husband. Hector will return to the field because he cannot remain only at home. That split is central to Troy's tragedy.",
      }),
      'zh-Hant-mandarin': track("trojan-war-hector-and-city", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 50,
        text: "在一連串戰鬥之中，赫克托耳回到特洛伊，讓城市不再只是被圍的目標，而是一個有人等待、有人恐懼的家。赫克托耳見到父親普里阿摩斯、母親赫卡柏、兄弟帕里斯與海倫，也見到妻子安德洛瑪刻。每一段對話都把戰爭的代價拉近：他是保衛城市的戰士，也是家族中的兒子、兄弟與丈夫。赫克托耳仍會回到戰場，因為他的身份不能只留在家中。這份分裂正是特洛伊悲劇的核心。",
      }),
    },
  },
  {
    sceneId: "trojan-war-death-of-patroclus",
    globalOrder: 49,
    storyId: "trojan-war",
    stepId: "death-of-patroclus",
    sourceStepIds: ["death-of-patroclus"],
    sourceIds: ["iliad-16-patroclus"],
    tracks: {
      en: track("trojan-war-death-of-patroclus", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 44,
        text: "After Achilles withdraws, the Greek ships come under growing pressure. Patroclus acts from friendship and urgency, but his action drives the story toward irreversible death. Wearing Achilles' armour, he leads the Myrmidons out. Zeus' weighing, Apollo's intervention, and Hector's final blow bring him down, and his death pulls Achilles back from withdrawal. After Patroclus dies, Achilles' return is inevitable.",
      }),
      'zh-Hant-mandarin': track("trojan-war-death-of-patroclus", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 52,
        text: "阿基里斯撤戰後，希臘船隊承受越來越大的壓力。帕特羅克洛斯的行動由友情與急迫推動，卻把故事推向不可逆轉的死亡。帕特羅克洛斯穿上阿基里斯的鎧甲，率密耳彌冬人出戰，希望逼退特洛伊攻勢。宙斯的權衡、阿波羅的干預與赫克托耳的最後一擊，合力把他帶到死亡邊緣；他失去鎧甲與力量，也使阿基里斯無法再把自己關在怒火之外。帕特羅克洛斯死後，阿基里斯重返戰場已成必然。",
      }),
    },
  },
  {
    sceneId: "trojan-war-achilles-and-hector",
    globalOrder: 50,
    storyId: "trojan-war",
    stepId: "achilles-and-hector",
    sourceStepIds: ["achilles-and-hector"],
    sourceIds: ["iliad-18-hephaestus-shield", "iliad-22-hector", "iliad-24-priam"],
    tracks: {
      en: track("trojan-war-achilles-and-hector", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 45,
        text: "After Patroclus dies, Achilles' anger changes direction. Thetis obtains new armour from Hephaestus, and the battlefield moves toward its heaviest duel. Achilles kills Hector outside Troy. It is a victory of force and grief-driven violence, but the Iliad does not end with triumph. The scene ends not with the sack of Troy but with return and mourning, binding the greatest killing to a shared recognition of death.",
      }),
      'zh-Hant-mandarin': track("trojan-war-achilles-and-hector", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 53,
        text: "帕特羅克洛斯死後，阿基里斯的怒火改變方向。忒提斯為兒子取得赫淮斯托斯打造的新甲，戰場也準備迎向最沉重的對決。阿基里斯追上赫克托耳，在特洛伊城外殺死他；這是力量的勝利，也是悲痛失控後的暴力。可是《伊利亞特》沒有用勝利結束，而讓普里阿摩斯跨越戰線，請求歸還兒子的遺體。因此，這段故事的終點不是陷城，而是歸還遺體與哀悼。史詩把最大的仇殺收束在共同承認死亡的時刻。",
      }),
    },
  },
  {
    sceneId: "trojan-war-death-of-achilles",
    globalOrder: 51,
    storyId: "trojan-war",
    stepId: "death-of-achilles",
    sourceStepIds: ["death-of-achilles"],
    sourceIds: ["apollodorus-epitome-5-3-7"],
    tracks: {
      en: track("trojan-war-death-of-achilles", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 43,
        text: "Achilles' death comes after the Iliad's funeral ending; the next part asks what happens when he too dies. Paris and Apollo kill Achilles with an arrow near the gate. Ajax recovers the body, but Achilles' arms ultimately go to Odysseus, turning the rivalry into a new fracture inside the coalition. After Achilles leaves the field, the coalition can still win, but the heroic world is already breaking.",
      }),
      'zh-Hant-mandarin': track("trojan-war-death-of-achilles", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 53,
        text: "阿基里斯的死亡發生在《伊利亞特》的葬禮結尾之後；接下來的故事追問：當他也死去，聯軍會變成甚麼？帕里斯與阿波羅在城門旁以箭殺死阿基里斯；忒提斯的兒子終於也被帶入戰死者行列。大埃阿斯搶回遺體，保存英雄尊嚴；可是阿基里斯的武器最後歸奧德修斯，兩人的競爭變成聯軍內部的新裂痕。阿基里斯退場後，聯軍仍能取勝，但英雄世界已經開始崩解。勝利越近，人的榮耀也越難保存完整。",
      }),
    },
  },
  {
    sceneId: "trojan-war-horse-sack-returns",
    globalOrder: 52,
    storyId: "trojan-war",
    stepId: "horse-sack-returns",
    sourceStepIds: ["horse-sack-returns"],
    sourceIds: ["apollodorus-epitome-5-8-14", "apollodorus-epitome-5-15-20", "apollodorus-epitome-5-21-23", "odyssey-8-wooden-horse"],
    tracks: {
      en: track("trojan-war-horse-sack-returns", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 41,
        text: "After the last great heroes fall, the end of the Trojan War advances through deception, warnings, and ignored omens rather than open assault. Odysseus devises the Wooden Horse, and Epeius builds it. Cassandra and Laocoon warn against it, but the horse enters Troy. The horse ends the war and begins the stories of returns and survivors. Victory does not restore the heroic world.",
      }),
      'zh-Hant-mandarin': track("trojan-war-horse-sack-returns", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 43,
        text: "最後幾位英雄相繼退場後，特洛伊戰爭的結局不再靠正面突破，而靠欺瞞、警告與被忽視的預兆推進。奧德修斯策劃特洛伊木馬，厄珀俄斯建造這件看似獻禮的武器。卡珊德拉警告其中藏有武裝者，拉奧孔主張摧毀，卻都無法阻止木馬進城。木馬讓戰爭結束，也讓歸途與倖存者的故事開始。勝利並沒有把英雄世界恢復原狀。",
      }),
    },
  },
  {
    sceneId: "aeneas-escape-troy",
    globalOrder: 53,
    storyId: "trojan-war",
    stepId: "horse-sack-returns",
    sourceStepIds: ["horse-sack-returns"],
    sourceIds: ["apollodorus-epitome-5-8-14", "apollodorus-epitome-5-15-20", "apollodorus-epitome-5-21-23", "odyssey-8-wooden-horse"],
    tracks: {
      en: track("aeneas-escape-troy", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 46,
        text: "The night Troy falls leaves not simply the victors' memory but also a path for survivors. Aeneas' episode separates from the same catastrophe. Once the sack begins, it is no longer only a victory scene but the breaking of a city. This source-specific scene focuses only on Aeneas: he is not made the new conqueror here, but escapes from the destruction carrying his father. The age after the heroes begins with scattering.",
      }),
      'zh-Hant-mandarin': track("aeneas-escape-troy", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 43,
        text: "特洛伊陷落之夜不單留下勝利者的記憶，也留下倖存者的出口。埃涅阿斯的段落從同一場毀滅中分出來。陷城一旦開始，特洛伊陷城便不再只是勝利場面，而是一座城市的斷裂。後英雄時代由這種散落感開始：城已失去，英雄世代逐漸退場，少數倖存者把戰爭餘波帶往別處，也把結局拉成新的漫長漂流、更遠遷徙與記憶。",
      }),
    },
  },
  {
    sceneId: "odyssey-war-to-lost-fleet",
    globalOrder: 54,
    storyId: "odyssey",
    stepId: "troy-departure",
    sourceStepIds: ["troy-departure", "bag-of-winds", "fleet-destroyed"],
    sourceIds: ["odyssey-9-departure-cyclops", "odyssey-10-winds", "odyssey-10-laestrygonians"],
    tracks: {
      en: track("odyssey-war-to-lost-fleet", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 40,
        text: "When Odysseus leaves Troy, the Trojan War is over, but Ithaca is not yet a reachable end. The homeward route begins as a fleet story. Aeolus once gives favourable winds, but the bag is opened and the voyage is thrown back into uncertainty. The Odyssey now asks not how to win, but whether Odysseus can preserve himself after losing ships, companions, and direction.",
      }),
      'zh-Hant-mandarin': track("odyssey-war-to-lost-fleet", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 42,
        text: "奧德修斯離開特洛伊時，特洛伊戰爭已經結束，但伊薩卡還不是可以抵達的終點。回鄉路先以艦隊形式展開。埃俄羅斯一度給予順風，彷彿歸途只差一點便能完成；可是同伴打開風袋，航程被重新拋回不確定之中。從這裏開始，《奧德賽》的問題不是如何取勝，而是失去船、同伴與方向後，奧德修斯還能否保存自己。",
      }),
    },
  },
  {
    sceneId: "odyssey-cyclops-cave",
    globalOrder: 55,
    storyId: "odyssey",
    stepId: "cyclops-cave",
    sourceStepIds: ["cyclops-cave"],
    sourceIds: ["odyssey-9-departure-cyclops"],
    tracks: {
      en: track("odyssey-cyclops-cave", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 45,
        text: "Odysseus' intelligence first becomes a deadly double edge on the return. Against Polyphemus, he must use speech and patience to escape the cave. He uses wine, the name \"Nobody,\" and the sheep to escape after blinding the Cyclops. But once offshore he reveals his real name, turning victory back into danger. The scene shows that Odysseus' best weapon can hurt him. Cunning saves lives; fame leaves divine anger behind.",
      }),
      'zh-Hant-mandarin': track("odyssey-cyclops-cave", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 53,
        text: "奧德修斯的智慧第一次在歸途中變成致命雙刃。面對波呂斐摩斯，他必須靠語言與耐心逃出洞穴。他以酒麻痺獨眼巨人，又用「無人」的假名讓求救失效；弄瞎獨眼巨人後，眾人藏在羊腹下逃離。可是離岸後，奧德修斯報上真名，讓勝利重新變成危機。這段經歷顯示奧德修斯最可靠的能力也會傷害他。機智救出人命，名聲卻把神怒留在船後；從此海路不再只是距離問題，而是父子復仇的漫長追索線。",
      }),
    },
  },
  {
    sceneId: "circe-and-the-command-to-seek-the-dead",
    globalOrder: 56,
    storyId: "odyssey",
    stepId: "circes-island",
    sourceStepIds: ["circes-island", "underworld-circe-command"],
    sourceIds: ["odyssey-10-circe", "odyssey-10-487-540-underworld-instructions"],
    tracks: {
      en: track("circe-and-the-command-to-seek-the-dead", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 45,
        text: "On Aeaea, Odysseus first faces the crisis of Circe. His companions fall under the shadow of transformation, and the route home is stopped again by divine power. Hermes helps him resist her, turning hostility into guidance. But Circe's next instruction is not a direct return home; Odysseus must go toward the underworld route and seek Tiresias among the rivers at the boundary. The homecoming is forced downward.",
      }),
      'zh-Hant-mandarin': track("circe-and-the-command-to-seek-the-dead", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 53,
        text: "埃阿埃亞上，奧德修斯先遇到喀耳刻的危機。同伴陷入變形陰影，回鄉路又一次被神性力量截停。赫耳墨斯幫助奧德修斯抵抗喀耳刻，使敵意轉為停留與指引。可是喀耳刻給出的下一步不是直接回家，而是下冥界：奧德修斯必須前往阿刻戎河、科庫托斯河、斯堤克斯與皮里弗勒革同河所構成的邊界，尋找忒瑞西阿斯的知識。歸途因此被迫向下。要回到家，奧德修斯先要承認活人世界已不足以回答他的問題。",
      }),
    },
  },
  {
    sceneId: "odysseus-consults-the-dead",
    globalOrder: 57,
    storyId: "odyssey",
    stepId: "underworld-prophecy",
    sourceStepIds: ["underworld-prophecy", "underworld-ocean-edge-rite", "underworld-debt-burial", "underworld-knowledge-beyond-death", "underworld-mother-cannot-hold", "underworld-war-dead-remember", "underworld-judgement-punishment"],
    sourceIds: ["odyssey-11-underworld", "odyssey-11-1-50-rite-at-ocean", "odyssey-10-541-574-elpenor-death", "odyssey-11-51-89-elpenor-burial", "odyssey-10-487-540-underworld-instructions", "odyssey-11-90-151-tiresias-prophecy", "odyssey-11-152-224-anticlea-shade", "odyssey-11-385-567-war-dead", "odyssey-11-568-629-minos-punished-dead"],
    tracks: {
      en: track("odysseus-consults-the-dead", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 44,
        text: "When Odysseus reaches the world's boundary, the Underworld is no longer a distant backdrop; it becomes a place he must listen to. Elpenor asks for burial, Tiresias names the prohibition on the homeward route, and Anticlea forces Odysseus to face his mother's death. This is not a side adventure but the centre of the return. Odysseus learns that homecoming carries memory and death as well as distance.",
      }),
      'zh-Hant-mandarin': track("odysseus-consults-the-dead", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 44,
        text: "奧德修斯到達世界邊界後，冥界不再是遙遠背景，而成為他必須聆聽的場所。下冥界在阿刻戎河一帶展開。埃爾佩諾耳要求安葬，忒瑞西阿斯以先知身份佔住儀式中心，安提克勒亞讓奧德修斯面對母親已死。這段不是支線冒險，而是歸途核心。奧德修斯在亡者聲音中明白：回家要付出的不再只是航海代價，還有記憶與死亡的重量。",
      }),
    },
  },
  {
    sceneId: "sirens-scylla-and-charybdis",
    globalOrder: 58,
    storyId: "odyssey",
    stepId: "sirens-song",
    sourceStepIds: ["sirens-song", "between-monsters"],
    sourceIds: ["odyssey-12-sea-trials"],
    tracks: {
      en: track("sirens-scylla-and-charybdis", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 45,
        text: "After receiving the route from the dead, Odysseus still faces dangers that courage alone cannot solve. Circe's instructions become the lifeline of the voyage. The Sirens tempt through song; Odysseus hears while bound, and his crew seals their ears. Then Scylla and Charybdis force him to choose the lesser disaster, accepting loss to avoid total destruction. The passage pushes Odysseus' intelligence into cruelty.",
      }),
      'zh-Hant-mandarin': track("sirens-scylla-and-charybdis", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 48,
        text: "從亡者那裏得到路線後，奧德修斯仍要穿過不能靠單純勇氣解決的危險。喀耳刻的指示成為他最後幾段航路的保命線。塞壬以歌聲誘惑，奧德修斯選擇被綁在桅杆上聆聽，同伴則以蠟封耳，讓知識慾望被約束而不毀船。接着，斯庫拉與卡律布狄斯迫使他在兩種災難之間選擇：避開吞船的漩渦，就要承受斯庫拉奪人的損失。這段航程把奧德修斯的智慧推到殘酷處。",
      }),
    },
  },
  {
    sceneId: "odyssey-cattle-of-sun",
    globalOrder: 59,
    storyId: "odyssey",
    stepId: "cattle-of-sun",
    sourceStepIds: ["cattle-of-sun"],
    sourceIds: ["odyssey-12-sea-trials"],
    tracks: {
      en: track("odyssey-cattle-of-sun", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 40,
        text: "Storm traps Odysseus and his companions on Thrinacia. Helios' cattle are there, and the prohibition is clear. When food runs out, the companions slaughter the untouchable cattle, and the return collapses from a crew voyage into catastrophe. After the ship is destroyed, Odysseus is the only survivor. Homecoming becomes a solitary drift carrying the memory of failure.",
      }),
      'zh-Hant-mandarin': track("odyssey-cattle-of-sun", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 37,
        text: "暴風把奧德修斯與同伴困在特里那基亞。赫利俄斯的神牛就在島上，禁令清楚得不能被誤解。糧食耗盡後，同伴仍宰殺不可觸碰的神牛，歸途因此由集體航行走向最後崩潰。船隊被毀後，奧德修斯成為唯一生還者。回家之路從此不再是帶領眾人，而是帶着失敗記憶獨自漂流。",
      }),
    },
  },
  {
    sceneId: "calypso-storm-and-phaeacians",
    globalOrder: 60,
    storyId: "odyssey",
    stepId: "calypsos-captive",
    sourceStepIds: ["calypsos-captive", "phaeacian-shore"],
    sourceIds: ["odyssey-5-release-storm", "odyssey-6-8-phaeacia", "odyssey-13-return"],
    tracks: {
      en: track("calypso-storm-and-phaeacians", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 43,
        text: "After losing the fleet, Odysseus is held by Calypso on Ogygia. The barrier to homecoming is no longer only the sea but the temptation of immortality. Zeus sends Hermes to permit departure, yet Poseidon shatters the raft with a storm. Odysseus reaches Scheria, where Nausicaa, Arete, and Alcinous bring him back into a world that can hear his story. He refuses immortality and survives the final storm.",
      }),
      'zh-Hant-mandarin': track("calypso-storm-and-phaeacians", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 55,
        text: "失去船隊後，奧德修斯在奧吉吉亞被卡呂普索留住。回鄉路在這裏不再是海上阻礙，而是不朽誘惑。宙斯派赫耳墨斯傳令，准許奧德修斯離開；可是波塞冬仍以波塞冬的風暴打碎他的筏子。奧德修斯漂到斯刻里亞，被瑙西卡發現，再由阿瑞忒與阿爾喀諾俄斯接納，費阿刻斯人的世界讓他重新成為能講述自身經歷的人。他拒絕永生、熬過最後風暴，終於接近伊薩卡。歸鄉即將從漂流變成身份、承認與家宅秩序的問題。",
      }),
    },
  },
  {
    sceneId: "odyssey-home-and-recognition",
    globalOrder: 61,
    storyId: "odyssey",
    stepId: "home-and-recognition",
    sourceStepIds: ["home-and-recognition"],
    sourceIds: ["odyssey-13-return", "odyssey-19-scar", "odyssey-21-bow", "odyssey-22-suitors", "odyssey-23-bed", "odyssey-24-peace"],
    tracks: {
      en: track("odyssey-home-and-recognition", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 41,
        text: "When Odysseus returns to Ithaca, return is not immediate restoration but a sequence of disguise and recognition. Athena arranges the disguise. He approaches Eumaeus and Telemachus, returns to the house occupied by the Suitors, is recognised by Eurycleia through the scar, and wins the Contest of the Bow before Penelope tests him through the bed. The ending is not simply revenge.",
      }),
      'zh-Hant-mandarin': track("odyssey-home-and-recognition", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 45,
        text: "奧德修斯回到伊薩卡後，重返伊薩卡不是立刻恢復王位，而是一連串辨認與偽裝。雅典娜安排偽裝，讓他先接近歐邁俄斯與忒勒馬科斯，再回到佩涅洛佩與求婚者所佔據的家中。歐律克勒亞以傷疤辨認他；彎弓競賽則把隱藏身份轉成公開行動。結局因此不是單純復仇，而是身份被逐層驗證。英雄回家，必須重新成為兒子、父親、丈夫與王。",
      }),
    },
  },
  {
    sceneId: "underworld-underworld-hermes-leads-shades",
    globalOrder: 62,
    storyId: "underworld",
    stepId: "underworld-hermes-leads-shades",
    sourceStepIds: ["underworld-hermes-leads-shades"],
    sourceIds: ["odyssey-24-1-14-hermes-shades"],
    tracks: {
      en: track("underworld-underworld-hermes-leads-shades", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 40,
        text: "At the end of the Odyssey, Hermes turns the view back toward the dead. With his golden wand he leads the Suitors toward the Underworld and the Asphodel Meadow, where they meet earlier war dead such as Achilles, Agamemnon, and Ajax. The homecoming therefore does not end only in the living household. This is the route of this book, not a universal afterlife claim.",
      }),
      'zh-Hant-mandarin': track("underworld-underworld-hermes-leads-shades", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 49,
        text: "《奧德賽》最後一卷把鏡頭再次轉向亡者。赫耳墨斯不再只是傳令者，而成為引導新亡魂的神。他以金杖召起求婚者的亡魂，帶他們穿過世界邊界，前往冥界中的阿斯福德爾草原。在那裏，新死者遇見較早死去的戰爭亡者：阿基里斯、阿伽門農與大埃阿斯仍在談論名聲、死亡與特洛伊記憶。這只是《奧德賽》此處描寫的亡魂行程，並不把所有死後傳統合併成同一個終點。",
      }),
    },
  }
]

const sceneById = new Map(timelineNarrationScenes.map((scene) => [scene.sceneId, scene]))

export function timelineNarrationSceneFor(sceneId: string | undefined) {
  if (!sceneId) return undefined
  return sceneById.get(sceneId)
}

export function timelineNarrationTrackForScene(sceneId: string | undefined, mode: TimelineNarrationMode) {
  if (!sceneId || mode === 'off') return undefined
  return timelineNarrationSceneFor(sceneId)?.tracks[mode]
}

export function defaultTimelineNarrationModeForLocale(locale: 'en' | 'zh-Hant'): TimelineNarrationMode {
  return locale === 'zh-Hant' ? 'zh-Hant-mandarin' : 'en'
}

export function timelineNarrationKey(sceneId: string, trackId: TimelineNarrationTrackId) {
  return `${sceneId}:${trackId}`
}

export function timelineNarrationDelayMs(track: TimelineNarrationTrack, speed: number, observedDurationSeconds?: number) {
  const rawDurationSeconds = observedDurationSeconds && Number.isFinite(observedDurationSeconds)
    ? observedDurationSeconds
    : track.estimatedDurationSeconds
  const effectiveSpeed = speed > 0 ? speed : 1
  return Math.max(1000, Math.ceil((rawDurationSeconds * 1000) / effectiveSpeed + 350))
}

export function timelineNarrationCharacterCount() {
  return timelineNarrationScenes.reduce((total, scene) => (
    total + timelineNarrationTrackIds.reduce((trackTotal, trackId) => (
      trackTotal + Array.from(scene.tracks[trackId].text).length
    ), 0)
  ), 0)
}
